import { getAuthToken } from './auth'
import { OfflineError, isNetworkFailure } from './errors'
import { track } from './analytics'

const BASE_URL = '/api'

// Tracks actual API reachability, not just navigator.onLine.
let apiHealth: 'unknown' | 'online' | 'offline' = 'unknown'

/**
 * How often a request is allowed through once the API is known unreachable.
 *
 * navigator.onLine is not sufficient on mobile. A phone can hold a cellular
 * association with no working data path, so onLine stays true and every
 * request runs the full FETCH_TIMEOUT_MS before aborting. That is what
 * produced the raw "timeout:GET /profile" screens: the app already knew it
 * was offline (the banner was showing, fed by these same events) while each
 * new request still spent 30s finding out again.
 *
 * So once apiHealth is 'offline', requests fail immediately. One request per
 * interval is still let through as a probe, which is how recovery is noticed
 * without a dedicated health endpoint.
 */
const OFFLINE_PROBE_INTERVAL_MS = 15_000
let lastProbeAt = 0

function setApiOffline() {
  if (apiHealth !== 'offline') {
    apiHealth = 'offline'
    // The request that discovered this counts as the first probe.
    lastProbeAt = Date.now()
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('app-offline'))
    }
  }
}

/**
 * Current API reachability, for components that mount after the fact.
 *
 * The app-offline/app-online events only fire on a transition, so a component
 * mounting while the app is already known offline never receives one. Seeding
 * from navigator.onLine alone then leaves it believing it is online — which is
 * how gated routes still rendered raw "timeout:GET …" text instead of their
 * OfflineGate.
 */
export function isApiOffline(): boolean {
  return apiHealth === 'offline'
}

// A regained connection should be retried at once rather than waiting out the
// probe interval. The browser only fires this when it sees a link change, so
// it complements the probe rather than replacing it.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    apiHealth = 'unknown'
    lastProbeAt = 0
  })
}

function setApiOnline() {
  if (apiHealth === 'offline') {
    apiHealth = 'online'
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('app-online'))
    }
  } else if (apiHealth === 'unknown') {
    apiHealth = 'online'
  }
}

/**
 * Native fetch has no timeout, so requests are capped. 30s across reads AND
 * writes: on Mozambican mobile (real 3G with a cold-start backend) legitimate
 * GETs regularly take 8-15s to first byte, and an earlier split-timeout
 * experiment fired false offline for those.
 *
 * RETRY_TIMEOUT_MS is the second, longer go given to a timed-out GET before we
 * are willing to call anything offline. See the retry block below.
 */
const FETCH_TIMEOUT_MS = 30_000
const RETRY_TIMEOUT_MS = 60_000

/** Anything slower than this gets reported. Set well above a healthy request
 *  so the event stays rare enough to read, and low enough to catch the
 *  8-15s-to-first-byte case our own timeout comment describes. */
const SLOW_REQUEST_MS = 5_000

const now = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now()

/**
 * Collapses ids out of a path so the event has bounded cardinality:
 * /programmes/cms51kzpv000q/details -> /programmes/:id/details
 */
function routeOf(path: string): string {
  return path
    .split('?')[0]!
    .split('/')
    .map(seg => (/^[a-z0-9]{16,}$/i.test(seg) || /^\d+$/.test(seg) ? ':id' : seg))
    .join('/')
}

/**
 * Reports where the time actually went.
 *
 * Students on Tmcel have hit intermittent "offline" for a long time, and
 * nobody could say whether those seconds were server time or wire time —
 * api.ts measured nothing at all. ttfb_ms is time to response headers (server
 * side); transfer_ms is the rest (payload over the link). If ttfb dominates,
 * no amount of bundle work helps and the fix is on the backend; if transfer
 * dominates, the payload is the problem. Best-effort: telemetry must never
 * break a request.
 */
function trackSlowRequest(input: {
  method: string
  path: string
  ttfbMs: number
  totalMs: number
  retried: boolean
  outcome: 'ok' | 'recovered' | 'timeout'
  bytes?: number | null
}): void {
  try {
    if (input.outcome === 'ok' && input.totalMs < SLOW_REQUEST_MS) return
    track('api.slow_request', {
      method:      input.method,
      route:       routeOf(input.path),
      ttfb_ms:     Math.round(input.ttfbMs),
      transfer_ms: Math.round(Math.max(0, input.totalMs - input.ttfbMs)),
      total_ms:    Math.round(input.totalMs),
      retried:     input.retried,
      outcome:     input.outcome,
      bytes:       input.bytes ?? null,
    })
  } catch {
    /* never let telemetry break a request */
  }
}

// Shared helper for both body-only and raw API methods.
async function rawRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ data: T; headers: Headers; status: number }> {
  // Skip the request when the browser already reports offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setApiOffline()
    throw new OfflineError(`offline:${method} ${path}`)
  }

  // Known-unreachable API: fail now instead of spending FETCH_TIMEOUT_MS
  // rediscovering it. Callers already branch on OfflineError, so this turns a
  // 30s hang into an immediate offline render. One probe per interval still
  // goes out so recovery is detected.
  if (apiHealth === 'offline') {
    const now = Date.now()
    if (now - lastProbeAt < OFFLINE_PROBE_INTERVAL_MS) {
      throw new OfflineError(`offline-known:${method} ${path}`)
    }
    lastProbeAt = now
  }

  const token = await getAuthToken()

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`

  const attempt = async (timeoutMs: number) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    const startedAt = now()
    try {
      const res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })
      // Headers are in, body may still be streaming. This split is the whole
      // point: TTFB is server time, the remainder is wire time, and only
      // measuring the total cannot tell you which one to go and fix.
      return { res, ttfbMs: now() - startedAt, startedAt }
    } finally {
      clearTimeout(timeoutId)
    }
  }

  let response: Response
  let ttfbMs = 0
  let startedAt = now()
  let retried = false

  try {
    const first = await attempt(FETCH_TIMEOUT_MS)
    response = first.res; ttfbMs = first.ttfbMs; startedAt = first.startedAt
  } catch (err) {
    // A TypeError from fetch means there is genuinely no route. That IS proof
    // of offline, it arrives fast, and retrying it would only waste time.
    if (isNetworkFailure(err)) {
      setApiOffline()
      throw new OfflineError(`network-unreachable:${method} ${path}`)
    }

    // A timeout proves nothing. A dead iOS data path and a slow Tmcel link
    // both abort at the same moment, and Mozambican students on 3G have been
    // hitting this intermittently for a long time: our own note above records
    // 8-15s to first byte from a cold-start backend, which puts a real request
    // uncomfortably close to the 30s ceiling. Treating that as "offline" told
    // students with working internet that they had none.
    //
    // So give it one more, longer go before concluding anything. GET only:
    // a POST that timed out may well have been processed server-side, and
    // replaying it would double-submit an attempt or a session.
    const isIdempotent = method === 'GET' || method === 'HEAD'
    if (!isIdempotent) {
      throw new OfflineError(`timeout:${method} ${path}`)
    }

    try {
      retried = true
      const second = await attempt(RETRY_TIMEOUT_MS)
      response = second.res; ttfbMs = second.ttfbMs; startedAt = second.startedAt
      trackSlowRequest({ method, path, ttfbMs, totalMs: now() - startedAt, retried: true, outcome: 'recovered' })
    } catch (retryErr) {
      if (isNetworkFailure(retryErr)) {
        setApiOffline()
        throw new OfflineError(`network-unreachable:${method} ${path}`)
      }
      // Two timeouts, the second with double the budget. Now it is evidence.
      setApiOffline()
      trackSlowRequest({ method, path, ttfbMs: 0, totalMs: now() - startedAt, retried: true, outcome: 'timeout' })
      throw new OfflineError(`timeout:${method} ${path}`)
    }
  }

  // Broadcast recovery if earlier requests had marked the app offline.
  setApiOnline()

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }))
    // Backend contract: student-visible errors return
    //   { error: 'machine_code', message: 'PT-MZ text' }
    // Prefer `message` so Error.message is student-safe by default. Callers
    // that need the machine code for branching should read err.data.error;
    // callers rendering a fallback for uncooperative responses should read
    // err.data?.message directly and swap in their own PT-MZ default rather
    // than trusting Error.message (which could be 'API request failed').
    // Zod failures come back as { error: 'Validation error', details: [...] },
    // with no `message`, so Error.message was the bare string "Validation
    // error" and the console showed nothing about WHICH field was rejected.
    // A PATCH sending a null into a non-nullable field cost an investigation
    // to find, when the answer was sitting in `details` all along.
    //
    // Only the failing field PATHS are appended, never the zod messages. The
    // paths are enough to locate the bug, and Error.message stays close to
    // student-safe: this shape only arises from a malformed request, which a
    // working UI does not produce, but a field name leaking is survivable
    // where "Expected string, received null" would not be.
    const fieldPaths = Array.isArray(errorData.details)
      ? [...new Set(
          errorData.details
            .map((d: { path?: unknown }) => Array.isArray(d?.path) ? d.path.join('.') : null)
            .filter((p: string | null): p is string => !!p),
        )]
      : []

    const baseMessage = errorData.message ?? errorData.error ?? 'API request failed'
    const error = new Error(
      fieldPaths.length > 0 ? `${baseMessage} (${fieldPaths.join(', ')})` : baseMessage,
    ) as Error & {
      status: number
      data: unknown
      headers?: Headers
    }
    error.status = response.status
    error.data   = errorData
    error.headers = response.headers
    throw error
  }

  const data = response.status === 204 ? (undefined as unknown as T) : await response.json()
  // Measured after the body is parsed, so totalMs covers transfer as well as
  // TTFB. trackSlowRequest drops anything under the threshold.
  trackSlowRequest({
    method, path, ttfbMs, totalMs: now() - startedAt, retried,
    outcome: 'ok',
    bytes: Number(response.headers.get('content-length')) || null,
  })
  return { data, headers: response.headers, status: response.status }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { data } = await rawRequest<T>(method, path, body)
  return data
}

export const api = {
  get:    <T>(path: string)                => request<T>('GET',    path),
  post:   <T>(path: string, body?: unknown) => request<T>('POST',   path, body),
  put:    <T>(path: string, body?: unknown) => request<T>('PUT',    path, body),
  patch:  <T>(path: string, body?: unknown) => request<T>('PATCH',  path, body),
  // Body is optional and rarely used, but DELETE /auth/me takes a typed
  // confirmation so the server can verify the caller knows which account
  // they are in. `request` already forwards a body for any method.
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),

  getRaw:    <T>(path: string)                => rawRequest<T>('GET',    path),
  postRaw:   <T>(path: string, body?: unknown) => rawRequest<T>('POST',   path, body),
  putRaw:    <T>(path: string, body?: unknown) => rawRequest<T>('PUT',    path, body),
  patchRaw:  <T>(path: string, body?: unknown) => rawRequest<T>('PATCH',  path, body),
  deleteRaw: <T>(path: string)                => rawRequest<T>('DELETE', path),
}

export type ApiError = Error & { status: number; data: unknown; headers?: Headers }

export { OfflineError } from './errors'
